<template>
    <base-modal
        @close="close"
        class="cz2__skin-tone-modal"
    >
        <div class="cz2__skin-tone-modal-content">
            <div>
                <div class="cz2__modal-popup__header">
                    {{ $t('BODY_TONE_MODAL_HEADING') }}
                </div>

                <p class="cz2__modal-popup__copy">
                    {{ $t('BODY_TONE_MODAL_COPY') }}
                </p>
            </div>

            <div
                role="radiogroup"
                :aria-label="$t('BODY_CHOOSE_TONE')"
                class="cz2__color-selector-wrapper"
            >
                <div
                    v-for="color in colorHexValues"
                    :key="color.color"
                >
                    <button
                        @click="selectColor(color)"
                        @keyup.space="selectColor(color)"
                        :class="[
                            { selected: selectedColor && selectedColor.code === color.code },
                        ]"
                        role="radio"
                        :aria-checked="selectedColor && selectedColor.code === color.code"
                        :aria-label="color.name"
                    >
                        <div
                            :style="{ background: `linear-gradient(#${color.color}, #${color.color})` }"
                        />

                        <transition name="cz2__fade">
                            <img
                                v-if="selectedColor?.code === color.code"
                                src="../assets/icons/color-drop.svg"
                                class="cz2__drop-icon"
                            >
                        </transition>
                    </button>
                </div>
            </div>

            <div class="cz2__skin-tone-modal__buttons">
                <btn
                    :disabled="!selectedColor"
                    primary
                    @click="loadNextStep"
                >
                    {{ $t('BODY_TONE_MODAL_CTA') }}
                </btn>
            </div>
        </div>
    </base-modal>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__skin-tone-modal {
            .cz2__modal-popup {
                z-index: 9999;
            }

            .cz2__skin-tone-modal-content {
                padding: 32px;
                max-width: 432px;
                display: flex;
                flex-direction: column;
                gap: 24px;
                text-align: center;

                .cz2__modal-popup__header {
                    margin-top: 16px;
                    line-height: 1;
                    text-transform: uppercase;
                    font-size: 28px;
                    font-style: italic;
                    color: black;
                }

                .cz2__modal-popup__copy {
                    margin-bottom: 0;
                    font-size: 16px;
                }

                .cz2__btn--primary {
                    width: 100%;
                    height: 40px;
                }

                .cz2__color-selector-wrapper {
                    padding: 0;
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    gap: 16px;

                    button {
                        width: 64px;
                        height: 64px;

                        &:focus-visible {
                            &:after {
                                top: -2px;
                                left: -2px;

                                box-sizing: content-box;
                                border: 2px solid black;
                            }
                        }

                        &:not(.unavailable):active::before,
                        &.selected::before {
                            border-color: #0874E4;
                            width: 100%;
                            height: 100%;
                        }

                        &:not(.unavailable):active::before,
                        &.selected::after {
                            border: 2px solid transparent;
                        }

                        &.selected {
                            div {
                                height: 90%;
                                width: 90%;
                                margin: 5%;
                            }
                        }

                        .cz2__drop-icon {
                            position: absolute;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                            width: 25px;
                            height: 25px;
                        }

                        @include respond-above(lg) {
                            width: 80px;
                            height: 80px;

                            .cz2__drop-icon {
                                width: 32px;
                                height: 32px;
                            }
                        }
                    }
                }

                @include respond-above(lg) {
                    .cz2__modal-popup__header {
                        font-size: 32px;
                    }

                    .cz2__modal-popup__copy {
                        font-size: 20px;
                    }

                    .cz2__btn--primary {
                        height: 48px;
                        font-size: 18px;
                    }
                }
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__skin-tone-modal {
            .cz2__skin-tone-modal-content {
                max-width: 624px;
                gap: 32px;

                .cz2__modal-popup__header {
                    line-height: 54px;
                    text-transform: none;
                    font-size: 56px;
                    font-style: normal;
                }

                .cz2__modal-popup__copy {
                    font-size: 20px;
                    line-height: 24px;
                }

                .cz2__color-selector-wrapper {
                    gap: 24px;

                    button {
                        width: 122px;
                        height: 122px;

                        .cz2__drop-icon {
                            width: 48px;
                            height: 48px;
                        }

                        &.selected {
                            div {
                                height: 84%;
                                width: 84%;
                                margin: 8%;
                            }
                        }
                    }
                }

                .cz2__btn--primary {
                    height: 64px;
                    font-size: 24px;
                }
            }
        }
    }
</style>

<script>
    import * as constants from '../constants';

    import BaseModal from './ui/BaseModal.vue';
    import Btn from './ui/Btn.vue';

    export default {
        components: {
            BaseModal,
            Btn,
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            nextGroup() {
                return this.$store.getters.nextGroup;
            },

            colors() {
                if (this.viewController == null) {
                    return [];
                }

                const placement = this.viewController.findPlacement(constants.PLACEMENT_SKIN_COLOR);

                let colors = placement.components.filter((c) => !c.placeholder && this.viewController.isAvailable(c));

                colors = colors.filter((color) => {
                    if (color.custom['fun-heads']) {
                        const heads = color.custom['fun-heads'].split(',');

                        const allHeads = heads.some((head) => {
                            const headPlacement = this.viewController.findPlacement(constants.PLACEMENT_HEAD);

                            const headComponent = headPlacement.components.find((c) => c.custom['fun-sku'] === head);

                            if (headComponent == null) {
                                return false;
                            }

                            // eslint-disable-next-line
                            const code = headComponent.description[0].code;

                            const option = this.viewController.blueprint.options.find((o) => o.code === code);

                            let inventory = this.viewController.emit('getInventory', headComponent);

                            if (inventory == null) {
                                inventory = option && option.inventory;
                            }

                            if (this.$store.state.customizer.options.alwaysAvailable) {
                                inventory = 100;
                            }

                            const available = inventory > 0;

                            if (!available) {
                                this.$store.dispatch('log', {
                                    level: 'error',
                                    message: `Head ${headComponent.code} is not in stock`,
                                });
                            }

                            return available;
                        });

                        if (!allHeads) {
                            this.$store.dispatch('log', {
                                level: 'error',
                                message: `One of heads ${color.custom['fun-heads']} is not in stock`,
                            });

                            return false;
                        }
                    }

                    return true;
                });

                return colors;
            },

            colorHexValues() {
                return this.colors.map((color) => ({
                    color: color.custom.hex,
                    placementIndependentCode: color.placementIndependentCode,
                    code: color.code,
                    name: color.description[0].name,
                    component: color,
                }));
            },

            selectedColor() {
                if (this.viewController == null) {
                    return null;
                }

                const color = this.viewController.selectedAtPlacement(constants.PLACEMENT_SKIN_COLOR);

                return (color !== null && !color.placeholder) ? color : null;
            },

            isFirstPop() {
                return this.viewController === this.$store.getters.popControllers[0];
            },

            skipLanding() {
                if (this.$store.state.options == null) {
                    return null;
                }

                return this.$store.state.options.skipLanding;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            hasBack() {
                return !this.isFirstPop || !this.skipLanding || this.isKiosk;
            },

            headGroupSeen() {
                return this.$store.state.customizer.seenGroups.includes(constants.GROUP_HEAD);
            },

            selectedBodyType() {
                if (this.viewController == null) {
                    return null;
                }

                const body = this.viewController.selectedAtPlacement(constants.PLACEMENT_BODY);

                return (body !== null && !body.placeholder) ? body : null;
            },
        },

        methods: {
            selectColor(hexColor) {
                const lastHead = this.viewController.selectedAtPlacement(constants.PLACEMENT_HEAD);

                const colorComponent = this.colors.filter((color) => color.placementIndependentCode === hexColor.placementIndependentCode)[0];

                if (!this.viewController.isAvailable(colorComponent)) {
                    return;
                }

                this.$store.dispatch('updateComponent', {
                    placement: constants.PLACEMENT_SKIN_COLOR,
                    code: colorComponent.code,
                }).then(() => {
                    this.pickHead(lastHead);
                }).catch(() => {
                });

                this.updateNavigation();
            },

            pickHead(lastHead) {
                const color = this.selectedColor;
                const skinTag = color.tags.find((t) => t.startsWith('skin-'));

                let bodyTag;

                if (lastHead && this.headGroupSeen) {
                    if (lastHead.tags) {
                        bodyTag = lastHead.tags.includes('head-male') ? 'head-male' : 'head-female';
                    }
                }

                if (bodyTag == null) {
                    const body = this.selectedBodyType;

                    if (body && body.tags) {
                        bodyTag = body.tags.includes('male') ? 'head-male' : 'head-female';
                    }
                }

                this.$store.dispatch('log', {
                    level: 'info',
                    message: `Selected color ${skinTag} and body ${bodyTag}`,
                });

                // Find a head.
                const heads = this.viewController.findPlacement(constants.PLACEMENT_HEAD);

                if (heads && heads.components) {
                    const head = heads.components.find((c) => {
                        if (c.tags.includes(skinTag) && c.tags.includes(bodyTag)) {
                            if (this.viewController.isAvailable(c)) {
                                return true;
                            }
                        }

                        return false;
                    });

                    if (head) {
                        this.$store.dispatch('log', {
                            level: 'info',
                            message: `Found a head ${head.code}`,
                        });

                        this.viewController.updateComponent(constants.PLACEMENT_HEAD, head.code);
                    }
                }

                // Check beards.
                const head = this.viewController.selectedAtPlacement(constants.PLACEMENT_HEAD);

                if (head && head.custom['fun-sku-beards']) {
                    const beardSku = head.custom['fun-sku-beards'];
                    const beardHead = this.viewController.findPlacement(constants.PLACEMENT_OTHER).components.find((c) => {
                        if (c.code.includes(beardSku)) {
                            return true;
                        }

                        return false;
                    });

                    if (beardHead) {
                        if (!this.viewController.isAvailable(beardHead)) {
                            const selectedBeard = this.viewController.selectedAtPlacement(constants.PLACEMENT_BEARD);
                            if (!selectedBeard.placeholder) {
                                const beardPlaceholder = this.viewController.findPlacement(constants.PLACEMENT_BEARD).components.find((x) => x.placeholder);

                                if (beardPlaceholder) {
                                    this.viewController.updateComponent(constants.PLACEMENT_BEARD, beardPlaceholder.code);
                                }
                            }
                        }
                    }
                }

                this.$store.dispatch('removeAlertGroup', {
                    code: 'Head',
                });
            },

            loadNextStep() {
                this.$store.dispatch('showGroup', this.nextGroup);

                this.close();
            },

            updateNavigation() {
                this.$emit('updateNavigation', {
                    hasBack: this.hasBack,
                    canGoBack: this.canGoBack,
                    hasNext: true,
                    canGoNext: true,
                });
            },

            close() {
                this.$store.dispatch('showSkinToneModal', false);
            },
        },
    };
</script>
